import type { CampaignCouponTemplateRecord, Currency, ReferralSettingsRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { appliesToModeFromLists, type AppliesToModeValues } from '../appliesToMode';
import { DISCOUNT_KIND } from '../discountKind';
import { resolvePromotionCopy, type PromotionCopyLocale } from '../suggestedPromotionCopy';
import { invalidateBrandReferralQueries } from './invalidateBrandReferralQueries';
import { ReferralDiscountFormFields } from './ReferralDiscountFormFields';
import { templateToReferralDiscountPayload, toReferralDiscountCreate } from './referralDiscountPayload';

const FORM_ID = 'edit-invite-discount-form';

const validationSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    discountKind: z.enum([DISCOUNT_KIND.amount, DISCOUNT_KIND.percentage]),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    salesItemIds: z.array(z.number()),
    salesItemTagIds: z.array(z.number()),
    appliesToMode: z.object({
      appliesToAllProducts: z.boolean(),
      filterByProducts: z.boolean(),
      filterByTags: z.boolean()
    })
  })
  .superRefine((value, ctx) => {
    if (value.discountKind === 'amount' && (value.discountAmount == null || value.discountAmount <= 0)) {
      ctx.addIssue({ code: 'custom', message: 'Enter a discount amount', path: ['discountAmount'] });
    }
    if (
      value.discountKind === 'percentage' &&
      (value.discountPercentage == null || value.discountPercentage <= 0 || value.discountPercentage > 100)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a percentage between 1 and 100',
        path: ['discountPercentage']
      });
    }
  });

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  brandId: number;
  serviceOrganizationId?: number;
  settings: ReferralSettingsRecord;
  templateId: number;
  currency: Currency;
  locale: PromotionCopyLocale;
  onRemove?: () => void;
};

const defaultsFromTemplate = (template: CampaignCouponTemplateRecord): FormValues => {
  const payload = templateToReferralDiscountPayload(template);
  return {
    name: template.namePublic,
    description: template.descriptionPublic,
    discountKind: template.discount && template.discount.amount > 0 ? 'amount' : 'percentage',
    discountAmount: template.discount?.amount ?? null,
    discountPercentage: template.discountPercentage ?? null,
    salesItemIds: payload.salesItemIds,
    salesItemTagIds: payload.salesItemTagIds,
    appliesToMode: appliesToModeFromLists({
      productCount: payload.salesItemIds.length,
      tagCount: payload.salesItemTagIds.length
    })
  };
};

const emptyDefaults = (): FormValues => ({
  name: '',
  description: '',
  discountKind: 'amount',
  discountAmount: null,
  discountPercentage: null,
  salesItemIds: [],
  salesItemTagIds: [],
  appliesToMode: appliesToModeFromLists({ productCount: 0, tagCount: 0 }) satisfies AppliesToModeValues
});

export const EditInviteDiscountSheet = ({
  open,
  onClose,
  brandId,
  serviceOrganizationId,
  settings,
  templateId,
  currency,
  locale,
  onRemove
}: Props) => {
  const template = settings.inviteDiscounts.find((item) => item.id === templateId);
  const invalidateInput = serviceOrganizationId != null ? { brandId, serviceOrganizationId } : { brandId };

  const { mutateAsync: patchSettings } = noddiAsync.usePatch({
    type: URLKeys.patchBrandReferralSettings,
    queryConfig: {
      onSuccess: async () => {
        await invalidateBrandReferralQueries(invalidateInput);
        onClose();
      }
    }
  });

  const form = useAppForm({
    defaultValues: template ? defaultsFromTemplate(template) : emptyDefaults(),
    validationLogic: revalidateLogic(),
    validators: { onDynamic: validationSchema },
    onSubmit: async ({ value }) => {
      if (!template) {
        return;
      }
      const { name, description } = resolvePromotionCopy({
        name: value.name,
        description: value.description,
        discountKind: value.discountKind,
        discountAmount: value.discountAmount,
        discountPercentage: value.discountPercentage,
        appliesToAllProducts: value.appliesToMode.appliesToAllProducts,
        currency,
        locale
      });
      if (!name) {
        return;
      }
      await patchSettings({
        brandId,
        serviceOrganizationId,
        inviteDiscounts: settings.inviteDiscounts.map((item) =>
          toReferralDiscountCreate(
            item.id === templateId
              ? {
                  ...templateToReferralDiscountPayload(item),
                  name,
                  description,
                  salesItemIds: value.salesItemIds,
                  salesItemTagIds: value.salesItemTagIds,
                  discount:
                    value.discountKind === 'amount' && value.discountAmount != null
                      ? { amount: value.discountAmount, currency }
                      : null,
                  discountPercentage: value.discountKind === 'percentage' ? value.discountPercentage : null
                }
              : templateToReferralDiscountPayload(item)
          )
        )
      });
    }
  });

  return (
    <form.AppForm>
      <NoddiSheet
        key={templateId}
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit invite discount'
        description='Changes apply to future referral activations.'
        footer={
          <>
            <form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />
            {onRemove ? (
              <NoddiButton
                variant='destructiveOutline'
                className='w-full'
                onClick={() => {
                  onRemove();
                  onClose();
                }}
              >
                Remove
              </NoddiButton>
            ) : null}
          </>
        }
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <ReferralDiscountFormFields
            form={form}
            currency={currency}
            locale={locale}
            nameField={{
              name: 'name',
              label: 'Name',
              tooltip: 'Shown on the coupon the customer receives.'
            }}
            descriptionField={{
              name: 'description',
              label: 'Description',
              tooltip: 'Short explanation of this discount for customers.'
            }}
          />
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
